<template>
  <v-card :loading="loading">
    <div class="px-4 py-6">
      <div class="mb-6 text-center" v-if="!hideAccountInfo">
        <UserAvatar
          class="mb-4"
          data-private
          :account="account"
          data-testid="recognitions-avatar-userAvatar"
        />
        <p
          class="headline blue-grey--text text--darken-3 ma-0 pa-0"
          data-testid="recognitions-text-userName"
        >
          {{ account.nickname || getFullName(account) }}
        </p>
        <p
          class="body2 blue-grey--text text--darken-1 ma-0 pa-0"
          data-testid="recognitions-text-userPosition"
        >
          {{ account.position }}
        </p>
      </div>
      <div class="d-flex justify-space-around align-center text-center px-5">
        <div>
          <p
            class="subtitle2 font-bold blue-grey--text text--darken-3 ma-0 pa-0"
            data-testid="recognitions-text-givenNumber"
          >
            {{ summary.given }}
          </p>
          <p
            class="subtitle2 blue-grey--text text--darken-1 ma-0 pa-0"
            data-testid="recognitions-text-givenTitle"
          >
            {{ $t('recognitions.given') }}
          </p>
        </div>
        <div>
          <p
            class="subtitle2 font-bold blue-grey--text text--darken-3 ma-0 pa-0"
            data-testid="recognitions-text-receivedNumber"
          >
            {{ summary.received }}
          </p>
          <p
            class="subtitle2 blue-grey--text text--darken-1 ma-0 pa-0"
            data-testid="recognitions-text-receivedTitle"
          >
            {{ $t('recognitions.received') }}
          </p>
        </div>
      </div>
      <Button
        v-if="canCreateRecognition"
        block
        color="primary"
        class="mt-6"
        :text="$t('recognitions.recognize')"
        @click="handleGiveRecognitionClick"
      />
    </div>
    <RecognitionDialog
      v-if="showCreateModal"
      :show="showCreateModal"
      :initialAccounts="[account._id]"
      @saved="onRecognitionCreated"
      @close="showCreateModal = false"
    />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import UserAvatar from '@/components/UserAvatar.vue'
import Button from '@/components/UI/Button.vue'
import RecognitionDialog from '@/components/RecognitionDialog.vue'

export default {
  name: 'RecognitionsSummary',
  mixins: [mixin, userMixin, trackerMixin],
  props: {
    dateRange: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    account: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hideAccountInfo: { type: Boolean, required: false, default: false },
    canCreateRecognition: { type: Boolean, required: false, default: false },
    section: { type: String },
  },

  components: {
    UserAvatar,
    RecognitionDialog,
    Button,
  },

  data() {
    return {
      summary: {
        given: 0,
        received: 0,
      },
      loading: false,
      showCreateModal: false,
    }
  },

  watch: {
    dateRange: {
      handler() {
        this.fetchAccountSummary()
      },
      deep: true,
    },

    account: {
      handler() {
        this.fetchAccountSummary()
      },
      deep: true,
    },
  },

  created() {
    this.fetchAccountSummary()
  },

  computed: {
    ...mapGetters(['organizationId']),

    accountId() {
      return this.account?._id ?? ''
    },
  },

  methods: {
    onRecognitionCreated() {
      this.showCreateModal = false
      this.$emit('created')
      this.fetchAccountSummary()
    },

    async fetchAccountSummary() {
      if (!this.accountId) return
      try {
        this.loading = true
        const filters = {}
        if (this.dateRange.from && this.dateRange.to)
          filters.createdAt = this.dateRange
        const response = await api.recognition.getAccountSummary(
          this.organizationId,
          this.accountId,
          { filters }
        )

        this.summary = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },
    handleGiveRecognitionClick() {
      this.trackRecognitionsNavigate({
        option: 'create',
        section: this.section,
      })
      this.showCreateModal = true
    },
  },
}
</script>
